import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

/** V42 — learners deleted in the last 24 h. Superadmin can restore; after the window they are purged. */
export const RecentlyDeletedDialog = ({ open, onClose, onRestored, canRestore }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get('/api/users/deleted').then(r => setRows(r.data.users || [])).catch(() => { toast.error('Could not load deleted users'); setRows([]); });
  }, [open]);

  const restore = async (u) => {
    setBusy(u.id);
    try {
      const r = await api.post(`/api/users/${u.id}/restore`);
      toast.success(r.data.message || 'Restored');
      setRows(rows.filter(x => x.id !== u.id));
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
    setBusy(null);
  };

  const hoursLeft = (u) => Math.max(0, Math.round((new Date(u.purge_after) - Date.now()) / 36e5));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="recently-deleted-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Trash2 className="h-4 w-4" />Recently deleted</DialogTitle>
          <p className="text-xs text-muted-foreground">Deleted learners are logged out instantly and hidden everywhere. They can be restored for 24 hours, then they are permanently removed.</p>
        </DialogHeader>
        {rows === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground" data-testid="recently-deleted-empty">Nothing deleted in the last 24 hours.</p>
        ) : (
          <ul className="divide-y max-h-[60vh] overflow-y-auto" data-testid="recently-deleted-list">
            {rows.map(u => (
              <li key={u.id} className="flex items-center gap-3 py-3" data-testid={`deleted-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{u.name || '—'} <span className="text-muted-foreground font-normal">· {u.email}</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Deleted {u.deleted_at ? formatDate(u.deleted_at) : ''} by <span className="font-medium">{u.deleted_by_name || 'admin'}</span>
                    <Badge variant="outline" className="ml-2 text-[10px]">{hoursLeft(u)} h left</Badge>
                  </p>
                </div>
                {canRestore && (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => restore(u)} disabled={busy === u.id} data-testid={`restore-user-${u.id}`}>
                    {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}Restore
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecentlyDeletedDialog;
